import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, X, UserPlus, KeyRound, SlidersHorizontal } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../lib/confirm";

/**
 * Modal that lists users belonging to a child lodge and lets the obedience
 * admin add/remove credentials for that lodge.
 */
const SECRETARY_PERMS = ["members", "events", "minutes", "documents", "certificates", "announcements", "messages", "petitions", "transfers", "mobile_app", "office_history", "votes"];
const TREASURER_PERMS = ["dues", "members_view"];
const ALL_PERMS = [...SECRETARY_PERMS, ...TREASURER_PERMS];
const DEFAULTS = { secretary: SECRETARY_PERMS, treasurer: TREASURER_PERMS };

export default function JurisdictionLodgeUsersModal({ lodge, onClose }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", name: "", office_role: "wm", permissions: [] });
  const [saving, setSaving] = useState(false);
  const [permsFor, setPermsFor] = useState(null);
  const [permsDraft, setPermsDraft] = useState([]);
  const [permsSaving, setPermsSaving] = useState(false);

  const savePerms = async (e) => {
    e.preventDefault();
    if (permsDraft.length === 0) return;
    setPermsSaving(true);
    try {
      await api.put(`/jurisdiction-lodges/${lodge.id}/users/${permsFor.id}`, { permissions: permsDraft });
      toast.success(t("users.permissionsUpdated"));
      setPermsFor(null);
      await load();
      window.dispatchEvent(new Event("sc-users-changed"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPermsSaving(false);
    }
  };

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/jurisdiction-lodges/${lodge.id}/users`);
      setUsers(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    const onKey = (e) => {
      if (e.key === "Escape" && !document.querySelector('[data-testid="confirm-dialog"]')) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/jurisdiction-lodges/${lodge.id}/users`, { ...form, role: "admin" });
      toast.success(t("jurisdiction.users.createdSuccess"));
      setShowForm(false);
      setForm({ username: "", password: "", name: "", office_role: "wm", permissions: [] });
      await load();
      window.dispatchEvent(new Event("sc-users-changed"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    const ok = await confirm({
      title: t("common.delete"),
      message: t("jurisdiction.users.deleteConfirm", { username: u.username }),
    });
    if (!ok) return;
    try {
      await api.delete(`/jurisdiction-lodges/${lodge.id}/users/${u.id}`);
      toast.success(t("jurisdiction.users.deletedSuccess"));
      await load();
      window.dispatchEvent(new Event("sc-users-changed"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div
        className="dialog-panel"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640, width: "100%" }}
        data-testid="jurisdiction-users-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="kv-label" style={{ fontSize: "0.62rem", letterSpacing: "0.16em" }}>
              {t("jurisdiction.title")}
            </div>
            <h2 className="dialog-title" style={{ margin: 0 }}>
              {t("jurisdiction.users.title")} · {lodge.name}
            </h2>
          </div>
          <button type="button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="flex justify-end mb-3">
          <button
            type="button"
            className="btn-primary-gold flex items-center gap-2"
            onClick={() => setShowForm(true)}
            data-testid="jurisdiction-users-add-btn"
          >
            <UserPlus size={14} /> {t("jurisdiction.users.add")}
          </button>
        </div>

        <div className="overflow-x-auto border border-[var(--cream-200)]">
          <table className="lodge-table" data-testid="jurisdiction-users-table">
            <thead>
              <tr>
                <th>{t("jurisdiction.users.name")}</th>
                <th>{t("login.username")}</th>
                <th>{t("users.role")}</th>
                <th className="text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {busy ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", padding: "2rem", fontStyle: "italic", color: "#858481" }}>
                    ...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", padding: "2rem", fontStyle: "italic", color: "#858481" }}>
                    {t("jurisdiction.users.empty")}
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} data-testid={`jurisdiction-user-row-${u.id}`}>
                    <td>{u.name}</td>
                    <td>
                      <span style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.06em" }}>
                        <KeyRound size={11} color="#9a7530" style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />
                        {u.username}
                      </span>
                    </td>
                    <td>
                      <span className="badge-grade" style={{ textTransform: "uppercase" }}>
                        {u.office_role === "secretary" ? t("users.roleSecretary")
                          : u.office_role === "treasurer" ? t("users.roleTreasurer")
                          : u.office_role === "wm" ? t("users.roleWm")
                          : u.role}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        {(u.office_role === "secretary" || u.office_role === "treasurer") && (
                          <button
                            className="btn-outline-gold"
                            onClick={() => { setPermsFor(u); setPermsDraft(u.permissions || []); }}
                            title={t("users.editPermissions")}
                            data-testid={`jurisdiction-user-perms-${u.id}`}
                          >
                            <SlidersHorizontal size={12} />
                          </button>
                        )}
                        <button
                          className="btn-danger"
                          onClick={() => remove(u)}
                          data-testid={`jurisdiction-user-delete-${u.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {permsFor && (
          <form className="mt-5 panel" onSubmit={savePerms} data-testid="jurisdiction-edit-perms-form">
            <div className="panel-head">{t("users.editPermissions")} · {permsFor.username}</div>
            <div className="panel-body">
              <div className="grid grid-cols-2 gap-1" style={{ maxHeight: 200, overflowY: "auto" }}>
                {ALL_PERMS.map((p) => (
                  <label key={p} className="flex items-center gap-2" style={{ fontSize: "0.78rem" }}>
                    <input
                      type="checkbox"
                      checked={permsDraft.includes(p)}
                      onChange={() => setPermsDraft(
                        permsDraft.includes(p) ? permsDraft.filter((x) => x !== p) : [...permsDraft, p]
                      )}
                      data-testid={`jurisdiction-edit-perm-${p}`}
                    />
                    <span>{t(`users.perm_${p}`)}</span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button type="button" className="btn-outline-gold" onClick={() => setPermsFor(null)}>
                  {t("common.cancel")}
                </button>
                <button type="submit" className="btn-primary-gold" disabled={permsSaving || permsDraft.length === 0} data-testid="jurisdiction-save-perms-btn">
                  {permsSaving ? "..." : t("common.save")}
                </button>
              </div>
            </div>
          </form>
        )}

        {showForm && (
          <form className="mt-5 panel" onSubmit={submit} data-testid="jurisdiction-user-form">
            <div className="panel-head">{t("jurisdiction.users.add")}</div>
            <div className="panel-body grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-lodge">{t("jurisdiction.users.name")} *</label>
                <input
                  className="input-lodge"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="jurisdiction-user-input-name"
                />
              </div>
              <div>
                <label className="label-lodge">{t("login.username")} *</label>
                <input
                  className="input-lodge"
                  required
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  autoComplete="off"
                  data-testid="jurisdiction-user-input-username"
                />
              </div>
              <div>
                <label className="label-lodge">{t("login.password")} *</label>
                <input
                  type="password"
                  className="input-lodge"
                  required
                  minLength={4}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  autoComplete="new-password"
                  data-testid="jurisdiction-user-input-password"
                />
              </div>
              <div>
                <label className="label-lodge">{t("users.role")}</label>
                <select
                  className="input-lodge"
                  value={form.office_role}
                  onChange={(e) => {
                    const r = e.target.value;
                    setForm({ ...form, office_role: r, permissions: DEFAULTS[r] ? [...DEFAULTS[r]] : [] });
                  }}
                  data-testid="jurisdiction-user-role-select"
                >
                  <option value="wm">{t("users.roleWm")}</option>
                  <option value="secretary">{t("users.roleSecretary")}</option>
                  <option value="treasurer">{t("users.roleTreasurer")}</option>
                </select>
              </div>
              {(form.office_role === "secretary" || form.office_role === "treasurer") && (
                <div className="md:col-span-2" data-testid="jurisdiction-user-permissions">
                  <label className="label-lodge">{t("users.permissions")}</label>
                  <div className="grid grid-cols-2 gap-1 mt-1" style={{ maxHeight: 180, overflowY: "auto" }}>
                    {ALL_PERMS.map((p) => (
                      <label key={p} className="flex items-center gap-2" style={{ fontSize: "0.78rem" }}>
                        <input
                          type="checkbox"
                          checked={form.permissions.includes(p)}
                          onChange={() => setForm({
                            ...form,
                            permissions: form.permissions.includes(p)
                              ? form.permissions.filter((x) => x !== p)
                              : [...form.permissions, p],
                          })}
                          data-testid={`perm-${p}`}
                        />
                        <span>{t(`users.perm_${p}`)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="md:col-span-2 flex justify-end gap-3 mt-2">
                <button type="button" className="btn-outline-gold" onClick={() => setShowForm(false)}>
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary-gold"
                  disabled={saving}
                  data-testid="jurisdiction-user-save-btn"
                >
                  {saving ? "..." : t("common.create")}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
